/**
 * Format currency for display.
 * Normalises tiny floating-point residues (e.g. -0.0000001) and negative zero
 * to a clean positive $0.00 so we never render "-$0.00".
 */
export const formatCurrency = (amount) => {
  const n = Number(amount) || 0;
  const rounded = Math.abs(n) < 0.005 ? 0 : n;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2
  }).format(rounded);
};

/**
 * App timezone - Central Standard Time
 */
export const APP_TIMEZONE = 'America/Chicago';

/**
 * Format a UTC timestamp for display in CST
 */
export const formatDateTime = (isoStr) => {
  if (!isoStr) return '';
  return new Date(isoStr).toLocaleString('en-US', {
    timeZone: APP_TIMEZONE,
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

/**
 * Format date for display (handles ISO and date-only strings)
 */
export const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const datePart = dateStr.includes('T') ? dateStr.split('T')[0] : dateStr;
  const [year, month, day] = datePart.split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

/**
 * Formats an address string into two lines
 * Line 1: Street address
 * Line 2: City, State ZIP
 * 
 * @param {string} address - Full address string
 * @returns {object|null} - { line1, line2 } or null if no address
 */
export const formatAddressTwoLines = (address) => {
  if (!address) return null;
  
  // Trim whitespace from the address
  const trimmedAddress = address.trim();
  if (!trimmedAddress) return null;
  
  // Pattern 1: "123 Main St, City, ST 12345" or "123 Main St, City, ST 12345-6789"
  const pattern1 = /^(.+?),\s*([^,]+,\s*[A-Z]{2}\s+\d{5}(?:-\d{4})?)$/i;
  const match1 = trimmedAddress.match(pattern1);
  if (match1) {
    return { line1: match1[1].trim(), line2: match1[2].trim() };
  }
  
  // Pattern 2: "123 Main St, City ST 12345" (no comma between city and state)
  const pattern2 = /^(.+),\s*(\w+[\s\w]*\s+[A-Z]{2}\s+\d{5}(?:-\d{4})?)$/i;
  const match2 = trimmedAddress.match(pattern2);
  if (match2) {
    return { line1: match2[1].trim(), line2: match2[2].trim() };
  }
  
  // Pattern 3: Multi-line with Suite/Unit - "123 Main St Suite 100, City, ST 12345"
  const pattern3 = /^(.+(?:Suite|Ste|Unit|Apt|#)\s*\d+[A-Za-z]*),\s*([^,]+,\s*[A-Z]{2}\s+\d{5}(?:-\d{4})?)$/i;
  const match3 = trimmedAddress.match(pattern3);
  if (match3) {
    return { line1: match3[1].trim(), line2: match3[2].trim() };
  }
  
  // Fallback: Try to split at the last comma before state abbreviation
  const fallbackPattern = /^(.+),\s*([A-Z]{2}\s+\d{5}(?:-\d{4})?)$/i;
  const fallbackMatch = trimmedAddress.match(fallbackPattern);
  if (fallbackMatch) {
    // The second capture might not include the city, so handle this case
    const parts = fallbackMatch[1].split(',');
    if (parts.length >= 2) {
      const city = parts.pop().trim();
      const street = parts.join(',').trim();
      return { line1: street, line2: `${city}, ${fallbackMatch[2].trim()}` };
    }
  }
  
  // If no pattern matches, return the full address as line1
  return { line1: trimmedAddress, line2: null };
};

/**
 * React component helper to render formatted address
 * @param {string} address - Full address string
 * @param {string} line1Class - CSS class for line 1
 * @param {string} line2Class - CSS class for line 2
 * @returns {JSX.Element|string} - Formatted address JSX or original string
 */
export const AddressDisplay = ({ address, line1Class = '', line2Class = '' }) => {
  const formatted = formatAddressTwoLines(address);
  
  if (!formatted) return null;
  
  if (formatted.line2) {
    return (
      <div className="space-y-0.5">
        <p className={line1Class}>{formatted.line1}</p>
        <p className={line2Class}>{formatted.line2}</p>
      </div>
    );
  }
  
  return <p className={line1Class}>{formatted.line1}</p>;
};
